import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen" data-testid="privacy-policy-page">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF5E6] via-[#FFFAF0] to-[#FFF8DC] opacity-70"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#06214c] mb-0 sm:mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="privacy-policy-title"
            >
              Privacy Policy
            </h1>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Introduction */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Introduction
              </h2>
              <p className="text-gray-700 leading-relaxed">
                At IP Protection Associate, we are committed to protecting your privacy and ensuring the security of your personal information.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website
                and use our services.
              </p>
            </div>

            {/* Information We Collect */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Information We Collect
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We collect information that you provide directly to us, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Personal identification information (name, email address, phone number)</li>
                  <li>Business information (company name, business address)</li>
                  <li>Trademark-related information (brand names, logos, descriptions)</li>
                  <li>Payment information (processed securely through third-party payment processors)</li>
                  <li>Communications between you and our team</li>
                </ul>
                <p>
                  We also automatically collect certain information when you visit our website, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Browser type and version</li>
                  <li>IP address and location data</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referring website addresses</li>
                  <li>Device information</li>
                </ul>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                How We Use Your Information
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Process and manage your trademark registration applications</li>
                  <li>Communicate with you about your applications and our services</li>
                  <li>Provide customer support and respond to your inquiries</li>
                  <li>Send important notices and updates about your services</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations and protect our legal rights</li>
                  <li>Prevent fraud and ensure security</li>
                </ul>
              </div>
            </div>

            {/* Information Sharing */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Information Sharing and Disclosure
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We do not sell your personal information. We may share your information in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>With government agencies (such as the USPTO) as necessary to process your trademark applications</li>
                  <li>With service providers who assist us in operating our business and providing services</li>
                  <li>When required by law or to protect our rights and safety</li>
                  <li>In connection with a business transfer or acquisition</li>
                  <li>With your consent or at your direction</li>
                </ul>
              </div>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Data Security
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against
                unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet
                or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            {/* Your Rights */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Your Rights and Choices
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  You have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access and receive a copy of your personal information</li>
                  <li>Correct inaccurate or incomplete information</li>
                  <li>Request deletion of your information (subject to legal requirements)</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Object to certain processing of your information</li>
                </ul>
                <p>
                  To exercise these rights, please contact us using the information provided below.
                </p>
              </div>
            </div>

            {/* Cookies */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar tracking technologies to enhance your experience on our website, analyze usage patterns,
                and deliver targeted content. You can control cookies through your browser settings, but disabling cookies may
                affect your ability to use certain features of our website.
              </p>
            </div>

            {/* Children's Privacy */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Children's Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information
                from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </div>

            {/* Changes to Privacy Policy */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy
                Policy on this page and updating the "Last Updated" date. Your continued use of our services after any changes
                constitutes acceptance of the updated policy.
              </p>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 p-6 bg-[#FFF5E6] rounded-lg">
                <p className="font-semibold text-[#06214c]">IP Protection Associate</p>
                <p className="text-gray-700">Email: info@ipprotectionassociate.com</p>
                <p className="text-gray-700">Phone: +1 (877) 946-4330</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
}